import { create } from 'zustand';
import { api, fromUnix, toQueryString } from '@/lib/api';
import type { ApiResponse, EmailMessage, EmailAccount } from '@nexus/shared/types';
import { toast } from './toastStore';

interface EmailState {
  messages: EmailMessage[];
  accounts: EmailAccount[];
  selected: EmailMessage | null;
  total: number;
  isLoading: boolean;
  error: string | null;
  fetchMessages: (params?: Record<string, string | number | undefined>) => Promise<void>;
  fetchAccounts: () => Promise<void>;
  send: (data: Record<string, unknown>) => Promise<void>;
  createAccount: (data: Record<string, unknown>) => Promise<void>;
  classify: (id: string) => Promise<void>;
  markRead: (id: string, isRead: boolean) => Promise<void>;
  deleteMessage: (id: string) => Promise<void>;
  setSelected: (id: string | null) => void;
  clearError: () => void;
}

function convertMsg(m: Record<string, unknown>): EmailMessage {
  return { ...m, createdAt: fromUnix(m.createdAt as number) } as EmailMessage;
}

function convertAcct(a: Record<string, unknown>): EmailAccount {
  return { ...a, createdAt: fromUnix(a.createdAt as number) } as EmailAccount;
}

export const useEmailStore = create<EmailState>((set, get) => ({
  messages: [],
  accounts: [],
  selected: null,
  total: 0,
  isLoading: false,
  error: null,

  fetchMessages: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const res = await api.get<ApiResponse<EmailMessage[]>>(`/api/email/messages${toQueryString(params)}`);
      set({
        messages: (res.data ?? []).map(m => convertMsg(m as unknown as Record<string, unknown>)),
        total: res.meta?.total ?? 0,
        isLoading: false,
      });
    } catch (err: unknown) {
      set({ error: (err as Error).message, isLoading: false });
    }
  },

  fetchAccounts: async () => {
    try {
      const res = await api.get<ApiResponse<EmailAccount[]>>('/api/email/accounts');
      set({ accounts: (res.data ?? []).map(a => convertAcct(a as unknown as Record<string, unknown>)) });
    } catch (err: unknown) {
      set({ error: (err as Error).message });
    }
  },

  send: async (data) => {
    try {
      await api.post('/api/email/send', data);
      toast.success('Email sent', `To ${(data as Record<string, string>).to}`);
    } catch (err) {
      toast.error('Failed to send email', (err as Error).message);
      throw err;
    }
  },

  createAccount: async (data) => {
    try {
      const res = await api.post<ApiResponse<EmailAccount>>('/api/email/accounts', data);
      if (res.data) set((s) => ({ accounts: [...s.accounts, convertAcct(res.data as unknown as Record<string, unknown>)] }));
      toast.success('Email account added');
    } catch (err) {
      toast.error('Failed to add email account', (err as Error).message);
      throw err;
    }
  },

  classify: async (id) => {
    try {
      await api.post(`/api/email/messages/${id}/classify`);
      toast.success('Email classified');
    } catch (err) {
      toast.error('Failed to classify email', (err as Error).message);
      throw err;
    }
  },

  markRead: async (id, isRead) => {
    try {
      const res = await api.patch<ApiResponse<EmailMessage>>(`/api/email/messages/${id}`, { isRead });
      if (res.data) {
        const updated = convertMsg(res.data as unknown as Record<string, unknown>);
        set((s) => ({
          messages: s.messages.map((m) => (m.id === id ? updated : m)),
          selected: s.selected?.id === id ? updated : s.selected,
        }));
      }
      toast.success(isRead ? 'Marked as read' : 'Marked as unread');
    } catch (err) {
      toast.error('Failed to update email', (err as Error).message);
      throw err;
    }
  },

  deleteMessage: async (id) => {
    try {
      await api.del(`/api/email/messages/${id}`);
      set((s) => ({
        messages: s.messages.filter((m) => m.id !== id),
        selected: s.selected?.id === id ? null : s.selected,
      }));
      toast.success('Email deleted');
    } catch (err) {
      toast.error('Failed to delete email', (err as Error).message);
      throw err;
    }
  },

  setSelected: (id) => {
    if (!id) {
      set({ selected: null });
      return;
    }
    const msg = get().messages.find((m) => m.id === id) ?? null;
    set({ selected: msg });
  },

  clearError: () => set({ error: null }),
}));
